import { Button } from "@legal-500/legal500-design-system";
import {
  APPROVAL_STATUS_BUCKETS,
  type ApprovalStatusBucket,
  type ApprovalStatusRunTotals,
} from "./approvalRequestStatus.js";
import { downloadReportCsv } from "./approvalRequestStatusCsv.js";
import { CopyableList } from "./CopyableList.js";
import type { ApprovalRequestStatusBatchProgress } from "./useApprovalRequestStatusBatch.js";
import styles from "../PipelineBrowser.module.css";

const BUCKET_LABELS: Record<ApprovalStatusBucket, string> = {
  decided: "Decided (status set, pipeline callback sent)",
  locked: "Locked by another user",
  notFound: "Not found",
  invalidStatus: "Invalid status (not decidable from its current status)",
  alreadyInTarget: "Already in the requested status",
  callbackFailed: "Callback failed (status reverted)",
};

const LIST_LABELS: Record<ApprovalStatusBucket, string> = {
  decided: "Decided ids",
  locked: "Locked ids",
  notFound: "Not found ids",
  invalidStatus: "Invalid status ids",
  alreadyInTarget: "Already in requested status ids",
  callbackFailed: "Callback failed ids",
};

type ApprovalRequestStatusRunReportProps = {
  totals: ApprovalStatusRunTotals;
  progress: ApprovalRequestStatusBatchProgress;
  stopped: boolean;
};

export function ApprovalRequestStatusRunReport({
  totals,
  progress,
  stopped,
}: ApprovalRequestStatusRunReportProps) {
  const { buckets, mismatches, unknown, guardUnconfirmed } = totals;
  const reportRows =
    APPROVAL_STATUS_BUCKETS.reduce((total, bucket) => total + buckets[bucket].length, 0) +
    mismatches.length +
    unknown.length;
  const unsent = progress.totalRows - progress.processedRows - progress.unknownRows;
  const mismatchLines = mismatches.map(mismatch => `${mismatch.id}\t${mismatch.status}`);
  const unknownLines = unknown.map(row => `${row.approvalRequestId}\t${row.status}`);

  return (
    <div className={styles.syncStatePanel}>
      <div className={styles.contentSectionTitle}>
        Result — applied{stopped ? " — stopped by user" : ""}
      </div>
      <div className={styles.syncStateMessage}>
        Processed {progress.processedRows} / {progress.totalRows} ids in {progress.chunk} /{" "}
        {progress.totalChunks} chunks.
        {progress.unknownRows > 0
          ? ` ${progress.unknownRows} ids were sent but their state is unknown.`
          : ""}
        {unsent > 0 ? ` ${unsent} ids were not sent.` : ""}
      </div>
      {guardUnconfirmed && (
        <div className={styles.warningBox} role="alert">
          approval-api did not confirm the NEW-only guard: a response had no statusMismatches field,
          so the guard was not enforced on that chunk and requests in other decidable statuses (for
          example SUSPENDED) may have been decided. Check the decided ids.
        </div>
      )}
      <table className={styles.reportTable}>
        <tbody>
          {APPROVAL_STATUS_BUCKETS.map(bucket => (
            <tr key={bucket}>
              <td>{BUCKET_LABELS[bucket]}</td>
              <td>{buckets[bucket].length}</td>
            </tr>
          ))}
          <tr>
            <td>Skipped, not NEW (never decided)</td>
            <td>{mismatches.length}</td>
          </tr>
          <tr>
            <td>Unknown, chunk failed (re-run to confirm)</td>
            <td>{unknown.length}</td>
          </tr>
        </tbody>
      </table>
      {APPROVAL_STATUS_BUCKETS.filter(bucket => buckets[bucket].length > 0).map(bucket => (
        <CopyableList key={bucket} label={LIST_LABELS[bucket]} lines={buckets[bucket]} />
      ))}
      {mismatchLines.length > 0 && (
        <CopyableList label="Skipped not NEW ids" lines={mismatchLines} />
      )}
      {unknownLines.length > 0 && <CopyableList label="Unknown ids" lines={unknownLines} />}
      <div className={styles.syncStateRow}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          disabled={reportRows === 0}
          onClick={() => downloadReportCsv(totals, new Date())}
        >
          Download report CSV ({reportRows} rows)
        </Button>
      </div>
    </div>
  );
}
